import { Observable } from 'rxjs';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { ApiUrlInjectionToken, PaginatedList } from '@mitsubishi/shared/data-access';
import { getRequestTraceHttpHeaders } from '@mitsubishi/shared/request-tracing';

import { PriceListSubfamily } from '../models/price-list-subfamily.model';
import {
  ExpiringMarkup,
  ExtendedWarrantyChangelog,
  MarkupChangelog,
  PriceListDetailsTableData,
  PriceListEntityType,
  PriceListWarranty,
  PriceListWarrantyMarkupEntry,
  SubFamilyChangelog,
} from '../models/price-list.model';
import { Revision } from '../models/revisions.model';
import { PriceListSubfamilyDetails } from '../models/subfamily-price-data.model';

@Injectable({
  providedIn: 'root',
})
export class PriceListService {
  private readonly baseUrl: string;
  private readonly apiServerUrl: string;

  constructor(
    private http: HttpClient,
    @Inject(ApiUrlInjectionToken) apiServerUrl: string
  ) {
    this.baseUrl = apiServerUrl + '/price-list';
    this.apiServerUrl = apiServerUrl;
  }

  downloadXlsMarkup(
    body: { subfamilies: Array<{ subfamilyId: number; familySapCode: string }>; organizationId: number },
    requestTraceId?: string
  ) {
    const headers = getRequestTraceHttpHeaders(requestTraceId);

    return this.http.post<string>(`${this.baseUrl}/markup/export`, body, { headers });
  }

  downloadXlsPriceList(body) {
    return this.http.post<string>(`${this.baseUrl}/export`, body);
  }

  downloadXlsExtendedWarranties(body, requestTraceId?: string) {
    const headers = getRequestTraceHttpHeaders(requestTraceId);

    return this.http.post<string>(`${this.baseUrl}/warranty/export`, body, { headers });
  }

  createMarkupRequest(body, requestTraceId?: string) {
    const headers = getRequestTraceHttpHeaders(requestTraceId);

    return this.http.post<string>(`${this.baseUrl}/markup/create_update_request`, body, { headers });
  }

  cancelMarkup(updateRequestId: string) {
    return this.http.post<string>(`${this.baseUrl}/markup/${updateRequestId}/abort`, {});
  }

  importMarkup(updateRequestId: string) {
    return this.http.post<string>(`${this.baseUrl}/markup/${updateRequestId}/apply`, {});
  }

  createWarrantyRequest(body, requestTraceId?: string) {
    const headers = getRequestTraceHttpHeaders(requestTraceId);

    return this.http.post<string>(`${this.baseUrl}/warranty/create_update_request`, body, { headers });
  }

  cancelWarranty(updateRequestId: string) {
    return this.http.post<string>(`${this.baseUrl}/warranty/${updateRequestId}/abort`, {});
  }

  importWarranty(updateRequestId: string) {
    return this.http.post<string>(`${this.baseUrl}/warranty/${updateRequestId}/apply`, {});
  }

  createExtendedWarrantiesMarkupImportRequest(formData: FormData, requestTraceId?: string) {
    const headers = getRequestTraceHttpHeaders(requestTraceId);

    return this.http.post<string>(`${this.baseUrl}/extend-warranty-markup/create_update_request`, formData, {
      headers,
    });
  }

  downloadXlsExtendedWarrantiesMarkup(
    body: { subfamilies: Array<{ subfamilyId: number; familySapCode: string }>; organizationId: number },
    requestTraceId?: string
  ) {
    const headers = getRequestTraceHttpHeaders(requestTraceId);

    return this.http.post<string>(`${this.baseUrl}/extend-warranty-markup/export`, body, { headers });
  }

  applyImportWarrantyMarkup(updateRequestId: string) {
    return this.http.post<string>(`${this.baseUrl}/extend-warranty-markup/${updateRequestId}/apply`, {});
  }

  abortImportWarrantyMarkup(updateRequestId: string) {
    return this.http.post<string>(`${this.baseUrl}/extend-warranty-markup/${updateRequestId}/abort`, {});
  }

  getPriceListSubFamilies(organizationId: number): Observable<PriceListSubfamily[]> {
    const paramsObj = {};
    paramsObj['organizationId'] = organizationId;
    const params = new HttpParams({ fromObject: paramsObj });

    return this.http.get<PriceListSubfamily[]>(`${this.baseUrl}/subfamilies`, { params });
  }

  getPriceListRevisions(subfamilyId: string, validityDate?: string): Observable<Revision[]> {
    const paramsObj = {};
    if (validityDate) paramsObj['validityDate'] = validityDate;
    const params = new HttpParams({ fromObject: paramsObj });
    return this.http.get<Revision[]>(`${this.baseUrl}/subfamilies/${subfamilyId}/revisions`, { params });
  }

  getPriceListFamiliesDetail(
    subfamilyId: number,
    organizationId: number,
    validityDate: string,
    sapCode: string,
    testPriceList: boolean,
    currencyCode?: string
  ): Observable<PriceListDetailsTableData> {
    const paramsObj = {};
    paramsObj['organizationId'] = organizationId;
    paramsObj['validityDate'] = validityDate;
    paramsObj['sapCode'] = sapCode;
    paramsObj['testPriceList'] = testPriceList;
    if (currencyCode) paramsObj['currencyCode'] = currencyCode;
    const params = new HttpParams({ fromObject: paramsObj });
    return this.http.get<PriceListDetailsTableData>(`${this.baseUrl}/subfamilies/${subfamilyId}`, { params });
  }

  draftMarkup(idMarkup: number, organizationId: number): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/markup/draft/${organizationId}/${idMarkup}`, {});
  }

  finalizeMarkup(
    idMarkup: number,
    organizationId: number,
    note?: string,
    validityDate?: string | null
  ): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/markup/final/${organizationId}/${idMarkup}`, { note, validityDate });
  }

  createMarkup(request: any): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/markup/create`, request);
  }

  updateMarkup(idMarkup: number, request: any): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/markup/edit/${idMarkup}`, request);
  }

  getCurrentPriceData(
    subfamilyId: number,
    organizationId: number,
    validityDate: string,
    sapCode: string,
    testPriceList: boolean,
    currencyCode?: string
  ): Observable<PriceListSubfamilyDetails> {
    const paramsObj = {};
    paramsObj['validityDate'] = validityDate;
    paramsObj['organizationId'] = organizationId;
    paramsObj['sapCode'] = sapCode;
    paramsObj['testPriceList'] = testPriceList;
    if (currencyCode) paramsObj['currencyCode'] = currencyCode;
    const params = new HttpParams({ fromObject: paramsObj });
    return this.http.get<PriceListSubfamilyDetails>(`${this.baseUrl}/subfamilies/${subfamilyId}/data`, { params });
  }

  deleteMarkup(idMarkup: number, organizationId: number): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/markup/delete/${organizationId}/${idMarkup}`);
  }

  createWarranty(request: any): Observable<null> {
    return this.http.post<null>(`${this.apiServerUrl}/warranty/create`, request);
  }

  updateWarranty(revisionId: number, request: any): Observable<null> {
    return this.http.put<null>(`${this.apiServerUrl}/warranty/update/${revisionId}`, request);
  }

  draftWarranty(subfamilyId: number, revisionId: number): Observable<null> {
    return this.http.patch<null>(`${this.apiServerUrl}/warranty/${subfamilyId}/${revisionId}/draft`, {});
  }

  finalizeWarranty(
    subfamilyId: number,
    revisionId: number,
    note?: string,
    validityDate?: string | null
  ): Observable<null> {
    return this.http.patch<null>(`${this.apiServerUrl}/warranty/${subfamilyId}/${revisionId}/final`, {
      note,
      validityDate,
    });
  }

  deleteWarranty(subfamilyId: number, revisionId: number): Observable<null> {
    return this.http.delete<null>(`${this.apiServerUrl}/warranty/${subfamilyId}/${revisionId}/delete`, {});
  }

  getExtendedWarrantyMarkup(
    subfamilyId: number,
    validityDate: string,
    organizationId: number
  ): Observable<PriceListWarranty[]> {
    const params = {
      validityDate,
      organizationId,
    };

    return this.http.get<PriceListWarranty[]>(`${this.baseUrl}/extended-warranty-markup/subfamily/${subfamilyId}`, {
      params,
    });
  }

  createExtendedWarrantyMarkup(request: PriceListWarrantyMarkupEntry): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/extended-warranty-markup/create`, request);
  }

  updateExtendedWarrantyMarkup(request: PriceListWarrantyMarkupEntry): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/extended-warranty-markup/update`, request);
  }

  draftExtendedWarrantyMarkup(extendedWarrantyMarkupId: string): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/extended-warranty-markup/${extendedWarrantyMarkupId}/draft`, {});
  }

  finalizeExtendedWarrantyMarkup(
    extendedWarrantyMarkupId: string,
    payload: {
      note?: string;
      validityDate?: string | null;
    }
  ): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/extended-warranty-markup/${extendedWarrantyMarkupId}/final`, payload);
  }

  deleteExtendedWarrantyMarkup(extendedWarrantyMarkupId: number | string): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/extended-warranty-markup/${extendedWarrantyMarkupId}/delete`, {});
  }

  downloadPriceList(
    subfamilyId: number,
    type: string,
    organizationId: string,
    validityDate: string,
    sapCode: string,
    testPriceList: boolean,
    classId: string,
    currencyCode?: string
  ) {
    const paramsObj = {};
    paramsObj['type'] = type;
    paramsObj['organizationId'] = organizationId;
    paramsObj['validityDate'] = validityDate;
    paramsObj['sapCode'] = sapCode;
    paramsObj['testPriceList'] = testPriceList;
    paramsObj['classId'] = classId;
    if (currencyCode) paramsObj['currencyCode'] = currencyCode;
    const params = new HttpParams({ fromObject: paramsObj });

    return this.http.get(`${this.baseUrl}/subfamilies/${subfamilyId}/export`, {
      params,
      responseType: 'blob',
      observe: 'response',
    });
  }

  loadChangeLogPricelists(
    request: { subfamilyId?: number; familySapCode?: string }[],
    viewFullHistory = false,
    page: number,
    pageSize: number
  ): Observable<PaginatedList<SubFamilyChangelog>> {
    return this.http.post<PaginatedList<SubFamilyChangelog>>(`${this.baseUrl}/changelog`, request, {
      params: { page, pageSize, viewFullHistory },
    });
  }

  loadChangelogMarkup(
    request: { subfamilyId?: number; familySapCode?: string }[],
    organizationId: number,
    viewFullHistory = false,
    page: number,
    pageSize: number
  ): Observable<PaginatedList<MarkupChangelog>> {
    return this.http.post<PaginatedList<MarkupChangelog>>(`${this.apiServerUrl}/markup/changelog`, request, {
      params: {
        viewFullHistory,
        page,
        pageSize,
        organizationId,
      },
    });
  }

  loadChangeLogExtendedWarranties(
    request: { subfamilyId?: number; familySapCode?: string }[],
    viewFullHistory = false,
    page: number,
    pageSize: number
  ): Observable<PaginatedList<ExtendedWarrantyChangelog>> {
    return this.http.post<PaginatedList<ExtendedWarrantyChangelog>>(
      `${this.apiServerUrl}/extended-warranty/changelog`,
      request,
      {
        params: { page, pageSize, viewFullHistory },
      }
    );
  }

  changeLogStatus(request: { subfamilyId: number; familySapCode: string }[]): Observable<number> {
    return this.http.post<number>(`${this.baseUrl}/countPriceListChanged`, request);
  }

  getExpiringMarkups(): Observable<ExpiringMarkup[]> {
    return this.http.get<ExpiringMarkup[]>(`${this.baseUrl}/get_expiring_markups`);
  }

  getDraftMarkups(): Observable<ExpiringMarkup[]> {
    return this.http.get<ExpiringMarkup[]>(`${this.baseUrl}/get_draft_markups`);
  }

  bulkFinalizeMarkup(markupIds: string[]): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/markup/bulk-final`, { markupIds });
  }

  bulkDeleteMarkup(markupIds: string[]): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/markup/bulk-delete`, { body: { markupIds } });
  }

  markMarkupAlertRead(): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/markup/alert-read`, {});
  }

  getCreateUpdateRequest(requestId: string, type: PriceListEntityType): Observable<PriceListSubfamily[]> {
    let requestPath = '';
    switch (type) {
      case 'markup':
        requestPath = 'markup';
        break;
      case 'extendedWarranties':
        requestPath = 'warranty';
        break;
      case 'extendedWarrantiesMarkup':
        requestPath = 'extend-warranty-markup';
        break;
    }

    return this.http.get<PriceListSubfamily[]>(`${this.baseUrl}/${requestPath}/get_create_update_request/${requestId}`);
  }
}
